import { useContext } from "react"
import { UserContext } from "../../../context/userContext"

//primeReact
import { Dialog } from "primereact/dialog"
import { ProgressSpinner } from "primereact/progressspinner"

//Formik
import { Form, Formik } from "formik"
import * as Yup from "yup"

//Formik helpers
import FormikTextInput from "../../../helpers/formikInputs/FormikTextInput"
import FormikCheckboxInput from "../../../helpers/formikInputs/FormikCheckboxInput"
import FormikMultiSelectInput from "../../../helpers/formikInputs/FormikMultiSelectInput"

//NewTemplateComponents
import NewTemplateDocumentUploadInput from "./NewTemplateDocumentUploadInput"
import NewTemplateSignerRoles from "./NewTemplateSignerRoles"
import { docSigningApiCreateTemplatePost } from "../documentsApi"
import HelloSign from "hellosign-embedded"
import { useEffect } from "react"
import { useState } from "react"

export default function NewTemplateForm(props) {
  const userCtx = useContext(UserContext)

  const [organizations, setOrganizations] = useState([])

  const defaultValues = {
    title: "",
    documents: [],
    signerRoles: [""],
    enforceSigningOrder: false,
    additionalOrganizations: [],
  }

  useEffect(() => {
    if (!!props.organizations) {
      setOrganizations(
        props.organizations.filter(
          (organization) =>
            organization.enterprise_id === userCtx.session.user.enterprise_id &&
            organization.organization_id !== userCtx.session.user.enterprise_id
        )
      )
    } else {
      setOrganizations([])
    }
  }, [userCtx.session.user.enterprise_id, props.organizations])

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Please enter a title"),
    documents: Yup.array().min(1, "Please add at least one PDF document"),
    signerRoles: Yup.array()
      .min(1, "Please specify at least one signer role")
      .max(5, "Only 5 signer roles are allowed")
      .test("emptyStrings", "Please specify all signer roles", (value) => {
        return value && !value.some((x) => !x || x === "")
      }),
  })

  function createRequestModel(values) {
    const formData = new FormData()
    formData.append("groupId", userCtx.session.user.organization_id)
    formData.append("title", values.title)
    formData.append(
      "createdBy",
      `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last} |${userCtx.session.user.staff_id}`
    )

    for (let i = 0; i < values.documents.length; i++) {
      const doc = values.documents[i]
      formData.append(`documents`, doc)
    }

    values.signerRoles.forEach((sr) => {
      formData.append("signerRoles", sr)
    })

    formData.append("enforceSigningOrder", values.enforceSigningOrder)

    values.additionalOrganizations.forEach((org) => {
      formData.append("additionalGroupIds", org.organization_id)
    })

    return formData
  }

  async function submitNewTemplate(values) {
    try {
      const requestModel = createRequestModel(values)
      const result = await docSigningApiCreateTemplatePost(
        userCtx.tokenDocumentSign,
        requestModel
      )

      if (result && result.template?.editUrl) {
        props.onSetShowNewTemplate(false)
        const client = new HelloSign({
          clientId: props.helloSignConfig.clientId,
          skipDomainVerification: props.helloSignConfig.mode === "Test",
        })

        client.open(result.template.editUrl)
        client.on("createTemplate", () => {
          userCtx.setNotificationInformation(
            `Your template is being created in the background. You'll be notified when it's 
            ready.`
          )
        })
      } else {
        return false
      }
    } catch (error) {
      console.error(error)
      userCtx.setNotificationError(
        "Unable to create the template. See console for details."
      )
      return false
    }
  }

  return (
    <Dialog
      header={"Create New Template"}
      visible={props.showNewTemplate}
      onHide={() => {
        props.onSetShowNewTemplate(false)
      }}
      style={{ width: "535px", height: "900px" }}
    >
      <Formik
        initialValues={defaultValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values) => {
          submitNewTemplate(values)
        }}
      >
        {({ isSubmitting, values }) =>
          isSubmitting ? (
            <div style={{ textAlign: "center" }}>
              <ProgressSpinner />
            </div>
          ) : (
            <Form>
              <div style={{ width: "100%" }}>
                <FormikTextInput
                  name="title"
                  label="Title"
                  helpText="Provide a title for your template. This will be used to identify your template throughout the application."
                />
                {
                  //When an enterprise is in scope, allow the user to specify organizations that should have access to the new template
                  userCtx.session.user.organization_id ===
                  userCtx.session.user.enterprise_id ? (
                    <FormikMultiSelectInput
                      name="additionalOrganizations"
                      label="Organizations"
                      helpText="Specify which organizations within this enterprise has access to the template"
                      options={organizations}
                      optionLabel="organization_name"
                    />
                  ) : null
                }
                <NewTemplateDocumentUploadInput
                  fieldName={"documents"}
                  label="Documents"
                  required={true}
                  helpText="Select the PDF documents that should be used in the template.
                These are the documents that participants and/or staff will sign.
                You can add 1 or more documents to the template."
                />
                <NewTemplateSignerRoles
                  rolesLength={values.signerRoles.length}
                  fieldName={"signerRoles"}
                  label="Signer Roles"
                  required={true}
                  helpText='Specify the types of individuals who will sign this template.
                These labels are used as placeholders (e.g., "Participant" or
                "Staff Member"). You must specify at least one signer and can
                specify up to a maximum of 5'
                />
                <FormikCheckboxInput
                  name="enforceSigningOrder"
                  label="Signing Order"
                  checkboxLabel="Enforce signing order?"
                  helpText="When checked, signers will be required to sign in the order added
                above for this template."
                />

                <div style={{ float: "right" }}>
                  <button type="submit" className="btn">
                    Next
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={{ marginLeft: "10px" }}
                    onClick={() => {
                      props.onSetShowNewTemplate(false)
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Form>
          )
        }
      </Formik>
    </Dialog>
  )
}
